import _ from "lodash";
import IdGenerator from "./IdGenerator";

const ACCOUNT_MERCHANDISE = "merchandise";
const ACCOUNT_CASH = "cash";

const CURRENCY_MERCHANDISE = "merchandise";

const ORDER_TYPE_PURCHASE = "purchase_order";
const ORDER_TYPE_RESERVE = "reserve_order";
const ORDER_TYPE_REFUND = "refund_order";
const ORDER_TYPE_GIFT_CARD = "gift_card_order";
const ORDER_TYPE_RECEIVE = "receive_order";
const ORDER_TYPE_GIVE = "give_order";

const ACCOUNT_TYPE_CASH = "01_cash";
const ACCOUNT_TYPE_MERCHANDISE = "02_merchandise";
const ACCOUNT_TYPE_CARD = "03_card";
const ACCOUNT_TYPE_GIFT_CARD = "04_gift_card";
const ACCOUNT_TYPE_OTHER = "05_other";

const SEPARATOR = "_@_";

const TRANSACTION_PREFIX = "TN";

class DailyReportService {
  collectTransactions(report) {
    let transactions = [];

    this._collectPurchaseOrder(transactions, report);
    this._collectReserveOrder(transactions, report);
    this._collectRefundOrder(transactions, report);
    this._collectGiftCardOrder(transactions, report);
    this._collectReceiveOrder(transactions, report);
    this._collectGiveOrder(transactions, report);

    return _.sortBy(transactions, "timestamp");
  }

  groupTransactions(transactions) {
    return _(transactions)
      .groupBy(trns => trns.accountType + SEPARATOR + trns.account)
      .toPairs()
      .sortBy(entry => entry[0])
      .map(entry => {
        entry[0] = entry[0].split(SEPARATOR)[1];
        return entry;
      })
      .value();
  }

  _collectPurchaseOrder(transactions, report) {
    _.forEach(report.purchaseOrderItems, (item, index) => {
      transactions.push({
        id: IdGenerator.generate(TRANSACTION_PREFIX),
        people: report.buyer,
        ref: item.vendor,
        accountType: ACCOUNT_TYPE_MERCHANDISE,
        account: ACCOUNT_MERCHANDISE,
        in: item.quantity,
        currency: CURRENCY_MERCHANDISE,
        timestamp: item.timestamp,
        dailyReportDate: report.date,
        dailyReportId: report.id,
        orderType: ORDER_TYPE_PURCHASE,
        orderIndex: index
      });

      item.payments.filter(isValidTrans).forEach(payment => {
        transactions.push({
          id: IdGenerator.generate(TRANSACTION_PREFIX),
          people: report.buyer,
          ref: item.vendor,
          accountType: this._getAccountType(payment.account),
          account: this._normalizeAccount(payment.account),
          out: payment.amount,
          currency: payment.currency,
          timestamp: item.timestamp,
          dailyReportDate: report.date,
          dailyReportId: report.id,
          orderType: ORDER_TYPE_PURCHASE,
          orderIndex: index
        });
      });
    });
  }

  _collectReserveOrder(transactions, report) {
    _.forEach(report.reserveOrderItems, (item, index) => {
      transactions.push({
        id: IdGenerator.generate(TRANSACTION_PREFIX),
        people: report.buyer,
        ref: item.vendor,
        accountType: ACCOUNT_TYPE_MERCHANDISE,
        account: this._normalizeAccount(ACCOUNT_MERCHANDISE),
        in: item.quantity,
        currency: CURRENCY_MERCHANDISE,
        timestamp: item.timestamp,
        dailyReportDate: report.date,
        dailyReportId: report.id,
        orderType: ORDER_TYPE_RESERVE,
        orderIndex: index
      });

      item.payments.filter(isValidTrans).forEach(payment => {
        transactions.push({
          id: IdGenerator.generate(TRANSACTION_PREFIX),
          people: report.buyer,
          ref: item.vendor,
          accountType: this._getAccountType(payment.account),
          account: this._normalizeAccount(payment.account),
          out: payment.amount,
          currency: payment.currency,
          timestamp: item.timestamp,
          dailyReportDate: report.date,
          dailyReportId: report.id,
          orderType: ORDER_TYPE_RESERVE,
          orderIndex: index
        });
      });
    });
  }

  _collectRefundOrder(transactions, report) {
    _.forEach(report.refundOrderItems, (item, index) => {
      transactions.push({
        id: IdGenerator.generate(TRANSACTION_PREFIX),
        people: report.buyer,
        ref: item.vendor,
        accountType: ACCOUNT_TYPE_MERCHANDISE,
        account: ACCOUNT_MERCHANDISE,
        out: item.quantity,
        currency: CURRENCY_MERCHANDISE,
        timestamp: item.timestamp,
        dailyReportDate: report.date,
        dailyReportId: report.id,
        orderType: ORDER_TYPE_REFUND,
        orderIndex: index
      });

      item.refunds.filter(isValidTrans).forEach(refund => {
        transactions.push({
          id: IdGenerator.generate(TRANSACTION_PREFIX),
          people: report.buyer,
          ref: item.vendor,
          accountType: this._getAccountType(refund.account),
          account: this._normalizeAccount(refund.account),
          in: refund.amount,
          currency: refund.currency,
          timestamp: item.timestamp,
          dailyReportDate: report.date,
          dailyReportId: report.id,
          orderType: ORDER_TYPE_REFUND,
          orderIndex: index
        });
      });
    });
  }

  _collectGiftCardOrder(transactions, report) {
    _.forEach(report.giftCardOrderItems, (item, index) => {
      item.giftCards.filter(isValidTrans).forEach(gc => {
        transactions.push({
          id: IdGenerator.generate(TRANSACTION_PREFIX),
          people: report.buyer,
          ref: item.vendor,
          accountType: this._getAccountType(gc.account),
          account: this._normalizeAccount(gc.account),
          in: gc.amount,
          currency: gc.currency,
          timestamp: item.timestamp,
          dailyReportDate: report.date,
          dailyReportId: report.id,
          orderType: ORDER_TYPE_GIFT_CARD,
          orderIndex: index
        });
      });

      item.payments.filter(isValidTrans).forEach(payment => {
        transactions.push({
          id: IdGenerator.generate(TRANSACTION_PREFIX),
          people: report.buyer,
          ref: item.vendor,
          accountType: this._getAccountType(payment.account),
          account: this._normalizeAccount(payment.account),
          out: payment.amount,
          currency: payment.currency,
          timestamp: item.timestamp,
          dailyReportDate: report.date,
          dailyReportId: report.id,
          orderType: ORDER_TYPE_GIFT_CARD,
          orderIndex: index
        });
      });
    });
  }

  _collectReceiveOrder(transactions, report) {
    _.forEach(report.receiveItems, (item, index) => {
      let merchandiseQuantity = item.merchandiseQuantity;
      if (merchandiseQuantity > 0) {
        transactions.push({
          id: IdGenerator.generate(TRANSACTION_PREFIX),
          people: report.buyer,
          ref: item.from,
          accountType: ACCOUNT_TYPE_MERCHANDISE,
          account: ACCOUNT_MERCHANDISE,
          in: merchandiseQuantity,
          currency: CURRENCY_MERCHANDISE,
          timestamp: item.timestamp,
          dailyReportDate: report.date,
          dailyReportId: report.id,
          orderType: ORDER_TYPE_RECEIVE,
          orderIndex: index
        });
      }

      item.transactions.filter(isValidTrans).forEach(trans => {
        transactions.push({
          id: IdGenerator.generate(TRANSACTION_PREFIX),
          people: report.buyer,
          ref: item.from,
          accountType: this._getAccountType(trans.account),
          account: this._normalizeAccount(trans.account),
          in: trans.amount,
          currency: trans.currency,
          timestamp: item.timestamp,
          dailyReportDate: report.date,
          dailyReportId: report.id,
          orderType: ORDER_TYPE_RECEIVE,
          orderIndex: index
        });
      });
    });
  }

  _collectGiveOrder(transactions, report) {
    _.forEach(report.giveItems, (item, index) => {
      let merchandiseQuantity = item.merchandiseQuantity;
      if (merchandiseQuantity > 0) {
        transactions.push({
          id: IdGenerator.generate(TRANSACTION_PREFIX),
          people: report.buyer,
          ref: item.to,
          accountType: ACCOUNT_TYPE_MERCHANDISE,
          account: this._normalizeAccount(ACCOUNT_MERCHANDISE),
          out: merchandiseQuantity,
          currency: CURRENCY_MERCHANDISE,
          timestamp: item.timestamp,
          dailyReportDate: report.date,
          dailyReportId: report.id,
          orderType: ORDER_TYPE_GIVE,
          orderIndex: index
        });
      }

      item.transactions.filter(isValidTrans).forEach(trans => {
        transactions.push({
          id: IdGenerator.generate(TRANSACTION_PREFIX),
          people: report.buyer,
          accountType: this._getAccountType(trans.account),
          account: this._normalizeAccount(trans.account),
          out: trans.amount,
          currency: trans.currency,
          ref: item.to,
          timestamp: item.timestamp,
          dailyReportDate: report.date,
          dailyReportId: report.id,
          orderType: ORDER_TYPE_GIVE,
          orderIndex: index
        });
      });
    });
  }

  // account name will normalized into UPPERCASE
  _normalizeAccount(acc) {
    let result = acc.toLowerCase();
    // if (result === ACCOUNT_MERCHANDISE || result === ACCOUNT_CASH) {
    //   result = `${acc}[${buyer}]`;
    // }

    return result.toLowerCase();
  }

  _getAccountType(accountName) {
    let lowerCase = accountName ? accountName.toLowerCase() : "";
    if (lowerCase === ACCOUNT_MERCHANDISE) {
      return ACCOUNT_TYPE_MERCHANDISE;
    }
    if (lowerCase === ACCOUNT_CASH) {
      return ACCOUNT_TYPE_CASH;
    }
    if (lowerCase.startsWith("gc")) {
      return ACCOUNT_TYPE_GIFT_CARD;
    }
    if (lowerCase.startsWith("c")) {
      return ACCOUNT_TYPE_CARD;
    }
    return ACCOUNT_TYPE_OTHER;
  }

  _trn(dailyReport, attributes) {
    return _.assign(
      {
        id: IdGenerator.generate(TRANSACTION_PREFIX),
        dailyReportId: dailyReport.id,
        people: dailyReport.buyer
      },
      attributes
    );
  }
}

function isValidTrans(transaction) {
  return transaction.account && transaction.amount && transaction.currency;
}

export default new DailyReportService();
